import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type AlertDialog = { text: string | null; icon: string | null };

type AlertContextType = {
  alertDialog: AlertDialog;
  showAlert: (text?: string | null, icon?: string | null) => void;
  clearAlert: () => void;
};

const AlertContext = createContext<AlertContextType | null>(null);

export const AlertProvider = ({ children }: PropsWithChildren) => {
  const [alertDialog, setAlertDialog] = useState<AlertDialog>({
    text: null,
    icon: null,
  });

  const showAlert = useCallback(
    (text?: string | null, icon?: string | null) =>
      setAlertDialog({ text: text ?? null, icon: icon ?? null }),
    [],
  );
  const clearAlert = useCallback(
    () => setAlertDialog({ text: null, icon: null }),
    [],
  );

  const value = useMemo(
    () => ({ alertDialog, showAlert, clearAlert }),
    [alertDialog, showAlert, clearAlert],
  );

  return (
    <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
  );
};

export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (context === null) {
    throw new Error("useAlertContext must be used within an AlertProvider");
  }
  return context;
};
